import React, { useState, useEffect, useRef } from 'react';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import CounterPost from './CounterPost';
import { Post } from '../../types/Post';
import { getMonsterImgPath } from '../../utils/monster';

import ProfileWithName from '../../components/ProfileWithName';
import { clockTime, isBattleRecord, monthDay, monthDayTime } from '../../utils/date';
import CustomDropdown from '../../components/CustomDropdown';
import SeasonSelect from '../Common/SeasonSelect';
import GroupSelect from '../Common/GroupSelect';
import MatchSummary from '../Common/MatchSummary';
import SiegeMapTimeline, { useSiegeMapAvailable } from '../Common/SiegeMapTimeline';
import { readGroups, fetchGroups } from '../../utils/groupCache';
import Caret from '../../components/Caret';
import { IconChart } from '../../components/Icons';
import BackButton from '../../components/BackButton';
import { guildImg } from '../../utils/guildImage';
import GradeBadge from '../../components/GradeBadge';

export interface PostListProps {
  posts: any[];
  allPosts?: any[]; // 시즌 전체 게시물 (그룹 통계 계산용)
  filteredPosts?: any[]; // 현재 필터 적용된 게시물
  /** 길드원 탭 통계용 — 공격/방어·승패·검색과 무관하게 그 그룹의 공격 기록 전부(점령전 전투기록). 없으면 filteredPosts */
  memberPosts?: any[];
  monsterMapping: any;
  user: any;
  searchValue: string;
  setSearchValue: (v: string) => void;
  onSearch: () => void;
  onPostClick: (post: any) => void;
  selectedPost: any;
  setSelectedPost: (post: any) => void;
  expandedPosts: Set<string>;
  togglePost: (postId: string) => void;
  onUploadClick: () => void;
  onPostDeleted?: () => void;
  selectedSeason: number;
  setSelectedSeason: (season: number) => void;
  selectedSiegeWar?: string;
  setSelectedSiegeWar?: (siegeWar: string) => void;
  selectedResult?: string;
  setSelectedResult?: (result: string) => void;
  /** 상대 길드 고르기(사용자 2026-10-31) — 'all' 또는 길드 이름. oppGuilds 는 고른 매치업의 상대 길드들 */
  selectedOpp?: string;
  setSelectedOpp?: (guild: string) => void;
  oppGuilds?: string[];
  onLoadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
  battleStep?: 'season' | 'group' | 'list';
  onSeasonSelect?: (season: number) => void;
  onGroupSelect?: (group: string) => void;
  onBackToSeason?: () => void;
  onBackToGroup?: () => void;
  prefetchedGroups?: any[];
  /** 그룹 카드에 찍을 승/패. SiegeBattleList 가 서버 집계값으로 채워 넘긴다.
      아래 로컬 state groupStats(고른 그룹 하나의 집계)와 다른 값이라 이름을 달리 둔다 */
  groupWinLoss?: { [groupName: string]: { wins: number; losses: number; winRate?: number; defenseRate?: number } };
  /** 내 길드명. 배너에 표시된다. 기본값은 기존 동작 유지용 */
  myGuildName?: string;
  /** 시즌 선택 화면에 띄울 목록. 없으면 SeasonSelect 기본값을 쓴다 */
  seasonList?: number[];
  /** 시즌 카드 문구. 점령전 회차처럼 숫자가 길 때 쓴다 */
  formatSeasonLabel?: (season: number) => string;
  /** 공덱/방덱 전환. 넘기지 않으면 표시하지 않는다 */
  logType?: number;
  setLogType?: (v: number) => void;
  /** 기록을 마지막으로 받아온 시각 (ISO). 없으면 아무것도 안 그린다 */
  syncedAt?: string | null;
}

/**
 * **마지막으로 받아온 시각.**
 *
 * 이 화면의 값이 언제 것인지 알 수 있어야 한다 — 수집이 멈춰 있어도 화면은 멀쩡해
 * 보이기 때문이다(실제로 프록시가 끊겨 이틀치가 안 들어온 적이 있다).
 */
const SyncedAt = ({ at, compact = false }: { at?: string | null; compact?: boolean }) => {
  if (!at) return null;
  return (
    <span style={{
      fontSize: compact ? 10 : 12,
      color: '#868e96',
      fontWeight: 600,
      whiteSpace: 'nowrap'
    }}>
      마지막 갱신 {monthDayTime(at, false)}
    </span>
  );
};

const PostList: React.FC<PostListProps> = ({
  posts: initialPosts,
  allPosts,
  filteredPosts,
  memberPosts,
  monsterMapping,
  user,
  searchValue,
  setSearchValue,
  onSearch,
  onPostClick,
  selectedPost,
  setSelectedPost,
  expandedPosts,
  togglePost,
  onUploadClick,
  onPostDeleted,
  selectedSeason,
  setSelectedSeason,
  selectedSiegeWar = 'all',
  setSelectedSiegeWar = () => {},
  selectedResult = 'all',
  setSelectedResult = () => {},
  selectedOpp = 'all',
  setSelectedOpp,
  oppGuilds = [],
  onLoadMore,
  hasMore,
  isLoading,
  battleStep = 'list',
  onSeasonSelect,
  onGroupSelect,
  onBackToSeason,
  onBackToGroup,
  prefetchedGroups,
  groupWinLoss,
  myGuildName = '설탕',
  seasonList,
  formatSeasonLabel,
  logType,
  setLogType,
  syncedAt
}) => {
  // 길드원 탭에서 사용할 게시물 (현재 그룹 필터 적용된 데이터)
  const postsForMemberStats = memberPosts || filteredPosts || initialPosts;
  const observerTarget = useRef<HTMLDivElement>(null);
  const [seasons, setSeasons] = useState<number[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [loadedGroupsSeason, setLoadedGroupsSeason] = useState<number | null>(null); // 이미 로드된 시즌 추적
  const [selectedGroupData, setSelectedGroupData] = useState<any>(null);
  const [groupStats, setGroupStats] = useState<{ wins: number; losses: number }>({ wins: 0, losses: 0 });
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [listTab, setListTab] = useState<'members' | 'records' | 'map'>('members');
  /** [거점 현황] 탭 — 그 점령전의 거점 지도 기록이 있을 때만(사용자 2026-10-13) */
  const hasMap = useSiegeMapAvailable(selectedGroupData?.siegeId);
  useEffect(() => { if (!hasMap && listTab === 'map') setListTab('members'); }, [hasMap, listTab]);
  const [expandedMemberKey, setExpandedMemberKey] = useState<string | null>(null);

  // 시즌 목록 가져오기
  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        const response = await fetch('/api/posts/seasons');
        if (response.ok) {
          const data = await response.json();
          setSeasons(data.seasons || []);
          // 시즌이 선택되지 않았고 시즌 목록이 있으면 첫 번째 시즌 선택
          if (data.seasons.length > 0 && !selectedSeason) {
            setSelectedSeason(data.seasons[0]);
          }
        }
      } catch (error) {
      }
    };
    fetchSeasons();
  }, []);

  // 그룹 목록 가져오기 (프리페치 데이터 우선 사용)
  useEffect(() => {
    if (!selectedSeason || selectedSeason === 0) return;
    if (loadedGroupsSeason === selectedSeason) return;

    // 프리페치된 데이터가 있으면 바로 사용 (네트워크 요청 없음)
    if (prefetchedGroups && prefetchedGroups.length > 0) {
      setGroups(prefetchedGroups);
      setLoadedGroupsSeason(selectedSeason);
      return;
    }

    /**
     * 저장된 카드가 있으면 **먼저 그린다.** 시즌을 고르면 카드가 뜨기까지 빈 화면이
     * 보였다 — 시즌 목록은 세션 저장소에 남겨 두는데 카드는 매번 새로 받았기 때문이다.
     */
    const cached = readGroups(selectedSeason);
    if (cached) setGroups(cached);

    const load = async () => {
      try {
        setGroups(await fetchGroups(selectedSeason));
        setLoadedGroupsSeason(selectedSeason);
      } catch (error) {
        if (cached) setLoadedGroupsSeason(selectedSeason);
      }
    };
    load();
  }, [selectedSeason, loadedGroupsSeason, prefetchedGroups]);

  // 선택된 그룹의 상세 정보 가져오기 (이미 로드된 groups에서 찾기)
  useEffect(() => {
    if (selectedSiegeWar && selectedSiegeWar !== 'all' && groups.length > 0) {
      const group = groups.find((g: any) => g.groupName === selectedSiegeWar);
      setSelectedGroupData(group || null);
    } else {
      setSelectedGroupData(null);
    }
  }, [selectedSiegeWar, groups]);

  // 선택된 그룹의 전체 통계 계산 (이미 로드된 allPosts에서 계산, 추가 fetch 없음)
  useEffect(() => {
    setGroupStats({ wins: 0, losses: 0 });

    if (!selectedSeason || !allPosts || allPosts.length === 0) {
      setIsLoadingStats(false);
      return;
    }

    // '전체' 는 그룹으로 거르지 않는다 — 예전에는 여기서 그냥 빠져나가 승/패가 안 나왔다
    let filtered = selectedSiegeWar === 'all'
      ? allPosts
      : allPosts.filter((post: any) => post.group === selectedSiegeWar);

    if (selectedResult === 'my' && user?.nickname) {
      filtered = filtered.filter((post: any) => post.nickname === user.nickname);
    }

    const wins = filtered.filter((post: any) => post.result === 'win').length;
    const losses = filtered.filter((post: any) => post.result === 'lose').length;
    setGroupStats({ wins, losses });
    setIsLoadingStats(false);
  }, [allPosts, selectedSiegeWar, selectedResult, user?.nickname]);

  // Intersection Observer로 스크롤 끝 감지
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          onLoadMore();
        }
      },
      {
        threshold: 0.5,
        rootMargin: '1000px' // 화면 아래 1000px 전에 미리 로딩
      }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, isLoading, onLoadMore]);
  // 기록 창이 떠 있는 동안 뒤 화면을 잠그고, 닫으면 보던 자리로 돌려놓는다
  useBodyScrollLock(!!selectedPost);



  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      position: 'relative',
      overflow: 'auto',
      height: 'auto'
    }}>

      <div style={{
        position: 'relative',
        zIndex: 2,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 'clamp(16px, 3vw, 20px) clamp(6px, 1.2vw, 8px)'
      }}>
        {/* 헤더 섹션 */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 'clamp(16px, 3vw, 20px)',
          padding: 'clamp(20px, 3.5vw, 24px) clamp(12px, 2.5vw, 16px)',
          marginBottom: 'clamp(20px, 3.5vw, 24px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          width: '100%',
          position: 'relative',
          zIndex: 100,
          overflow: 'visible'
        }}>
          {/* 전투기록 제목 - list가 아닐 때만 표시 */}
          {battleStep !== 'list' && (
            <div style={{ textAlign: 'center' }}>
              <h1 style={{
                fontSize: 'clamp(22px, 4.5vw, 26px)',
                fontWeight: 600,
                margin: 0,
                marginBottom: 'clamp(6px, 1.5vw, 8px)',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                display: 'inline-block',
                width: '100%',
                letterSpacing: '-0.8px'
              }}>전투기록</h1>
              <p style={{
                color: '#999',
                fontSize: 'clamp(10px, 2vw, 12px)',
                margin: 0,
                fontWeight: 500
              }}>점령전 중 일정한 주기로 자동으로 업데이트됩니다</p>
            </div>
          )}

          {/* 전투기록 리스트 헤더 - list 단계에서만 표시 */}
          {battleStep === 'list' && (
            <>
              {/* 머리 줄 — 왼쪽 뒤로 가기, 오른쪽 마지막 갱신. 그 아래 매치업 (사용자 시안) */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'clamp(8px, 2vw, 12px)', marginBottom: 'clamp(8px, 2vw, 10px)' }}>
                <BackButton onClick={onBackToGroup} />
                {syncedAt && <SyncedAt at={syncedAt} compact />}
              </div>

              {/* 매치업 — 점령전 한 판을 골랐고 게임 숫자(매치업 현황)가 있으면 길드를 골라 보는 판 하나로, 아니면 예전 배너 + 승/패 상자 */}
              {(() => {
                // 길드 매치업 배너
                const banner = (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 'clamp(12px, 3vw, 16px)',
                background: 'linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%)',
                borderRadius: 'clamp(10px, 2.5vw, 14px)',
                padding: 'clamp(10px, 2.5vw, 14px) clamp(14px, 3.5vw, 20px)',
                gap: 'clamp(10px, 2.5vw, 16px)',
                boxShadow: '0 2px 10px rgba(102,126,234,0.1)',
                border: '1px solid rgba(102,126,234,0.15)',
                overflowX: 'auto',
              }}>
                {/* 내 길드 */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'clamp(4px, 1vw, 6px)', flexShrink: 0 }}>
                  <img loading="lazy"
                    src={guildImg(myGuildName)}
                    alt={myGuildName}
                    style={{ width: 'clamp(32px, 8vw, 40px)', height: 'clamp(32px, 8vw, 40px)', borderRadius: 8, objectFit: 'cover', border: '2px solid rgba(102,126,234,0.3)', flexShrink: 0 }}
                    onError={(e) => { e.currentTarget.src = '/images/unknown2.png'; }}
                  />
                  <span style={{ fontSize: 'clamp(10px, 2.5vw, 12px)', fontWeight: 700, color: '#4f46e5', whiteSpace: 'nowrap', textAlign: 'center' }}>{myGuildName}</span>
                </div>

                {/* 상대 길드가 있을 때만 VS 를 그린다 — '전체' 에서는 "설탕 VS" 만 남았다 */}
                {selectedGroupData?.opponentGuilds?.length > 0 && (
                  <span style={{ fontSize: 'clamp(10px, 2.5vw, 13px)', fontWeight: 600, color: '#667eea', letterSpacing: '2px', flexShrink: 0 }}>VS</span>
                )}
                {/* 상대 길드들 */}
                {selectedGroupData?.opponentGuilds?.length > 0 ? (
                  selectedGroupData.opponentGuilds.map((guild: string, i: number) => (
                    <React.Fragment key={guild}>
                      {i > 0 && (
                        <span style={{ fontSize: 'clamp(10px, 2.5vw, 13px)', fontWeight: 600, color: '#667eea', letterSpacing: '2px', flexShrink: 0 }}>VS</span>
                      )}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'clamp(4px, 1vw, 6px)', flexShrink: 0 }}>
                        <img loading="lazy"
                          src={guildImg(guild)}
                          alt={guild}
                          style={{ width: 'clamp(32px, 8vw, 40px)', height: 'clamp(32px, 8vw, 40px)', borderRadius: 8, objectFit: 'cover', border: '2px solid rgba(0,0,0,0.1)', flexShrink: 0 }}
                          onError={(e) => { e.currentTarget.src = '/images/unknown2.png'; }}
                        />
                        <span style={{ fontSize: 'clamp(10px, 2.5vw, 12px)', fontWeight: 700, color: '#374151', whiteSpace: 'nowrap', textAlign: 'center' }}>{guild}</span>
                      </div>
                    </React.Fragment>
                  ))
                ) : null}
              </div>
                );
                const oldBoxes = (() => {
                const wins = groupStats.wins;
                const losses = groupStats.losses;
                const total = wins + losses;
                const winRate = total > 0 ? Math.round((wins / total) * 100) : 0;

                return (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 'clamp(8px, 2vw, 12px)',
                    marginBottom: 'clamp(16px, 3vw, 20px)',
                    flexWrap: 'wrap'
                  }}>
                    {/* 승리 박스 */}
                    <div style={{
                      background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.15) 0%, rgba(33, 150, 243, 0.08) 100%)',
                      borderRadius: 'clamp(8px, 1.5vw, 10px)',
                      padding: 'clamp(6px, 1.2vw, 8px) clamp(10px, 2vw, 14px)',
                      border: '2px solid rgba(33, 150, 243, 0.3)',
                      boxShadow: '0 2px 8px rgba(33, 150, 243, 0.12)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 'clamp(2px, 0.5vw, 3px)'
                    }}>
                      <span style={{
                        fontSize: 'clamp(9px, 1.8vw, 10px)',
                        fontWeight: 600,
                        color: '#1976d2',
                        letterSpacing: '0.2px'
                      }}>승리</span>
                      <span style={{
                        fontSize: 'clamp(14px, 3vw, 16px)',
                        fontWeight: 600,
                        color: '#2196F3',
                        textShadow: '0 1px 3px rgba(33, 150, 243, 0.2)'
                      }}>{wins}</span>
                    </div>

                    {/* 패배 박스 */}
                    <div style={{
                      background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.15) 0%, rgba(244, 67, 54, 0.08) 100%)',
                      borderRadius: 'clamp(8px, 1.5vw, 10px)',
                      padding: 'clamp(6px, 1.2vw, 8px) clamp(10px, 2vw, 14px)',
                      border: '2px solid rgba(244, 67, 54, 0.3)',
                      boxShadow: '0 2px 8px rgba(244, 67, 54, 0.12)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 'clamp(2px, 0.5vw, 3px)'
                    }}>
                      <span style={{
                        fontSize: 'clamp(9px, 1.8vw, 10px)',
                        fontWeight: 600,
                        color: '#d32f2f',
                        letterSpacing: '0.2px'
                      }}>패배</span>
                      <span style={{
                        fontSize: 'clamp(14px, 3vw, 16px)',
                        fontWeight: 600,
                        color: '#f44336',
                        textShadow: '0 1px 3px rgba(244, 67, 54, 0.2)'
                      }}>{losses}</span>
                    </div>

                    {/* 승률 박스 */}
                    <div style={{
                      background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(76, 175, 80, 0.08) 100%)',
                      borderRadius: 'clamp(8px, 1.5vw, 10px)',
                      padding: 'clamp(6px, 1.2vw, 8px) clamp(10px, 2vw, 14px)',
                      border: '2px solid rgba(76, 175, 80, 0.3)',
                      boxShadow: '0 2px 8px rgba(76, 175, 80, 0.12)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 'clamp(2px, 0.5vw, 3px)'
                    }}>
                      <span style={{
                        fontSize: 'clamp(9px, 1.8vw, 10px)',
                        fontWeight: 600,
                        color: '#388e3c',
                        letterSpacing: '0.2px'
                      }}>승률</span>
                      <span style={{
                        fontSize: 'clamp(14px, 3vw, 16px)',
                        fontWeight: 600,
                        color: '#4caf50',
                        textShadow: '0 1px 3px rgba(76, 175, 80, 0.2)'
                      }}>{winRate}%</span>
                    </div>
                  </div>
                );
                })();
                const old = <>{banner}{oldBoxes}</>;
                return selectedGroupData?.siegeId
                  ? <MatchSummary siegeId={selectedGroupData.siegeId} fallback={old} compact={true} />
                  : old;
              })()}
            </>
          )}
        </div>

        {/* 시즌 선택 카드 영역 */}
        {battleStep === 'season' && onSeasonSelect && (
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 'clamp(16px, 3vw, 20px)',
            padding: 'clamp(16px, 3vw, 20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            width: '100%',
            minHeight: 'clamp(260px, 50vh, 300px)',
            position: 'relative',
            zIndex: 1
          }}>
            <SeasonSelect onSelect={onSeasonSelect} seasons={seasonList} formatLabel={formatSeasonLabel} />
          </div>
        )}

        {/* 그룹 선택 카드 영역 */}
        {battleStep === 'group' && onGroupSelect && onBackToSeason && (
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 'clamp(16px, 3vw, 20px)',
            padding: 'clamp(16px, 3vw, 20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            width: '100%',
            minHeight: 'clamp(260px, 50vh, 300px)',
            position: 'relative',
            zIndex: 1
          }}>
            <GroupSelect
              season={selectedSeason}
              onSelect={onGroupSelect}
              onBack={onBackToSeason}
              groupStats={groupWinLoss}
            />
          </div>
        )}

        {/* 게시글 목록 - list 단계에서만 표시 */}
        {battleStep === 'list' && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 'clamp(16px, 3vw, 20px)',
          padding: 'clamp(10px, 2vw, 12px)',
          paddingBottom: 'clamp(20px, 4vw, 24px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          width: '100%',
          minHeight: 'clamp(260px, 50vh, 300px)',
          position: 'relative',
          zIndex: 1
        }}>
          {/* 탭 UI */}
          <div style={{
            display: 'flex',
            width: '100%',
            marginBottom: 'clamp(16px, 3vw, 20px)',
            borderBottom: '2px solid #eee'
          }}>
            <button
              onClick={() => setListTab('members')}
              data-subtab=""
              style={{
                flex: 1,
                padding: 'clamp(10px, 2vw, 12px) 0',
                border: 'none',
                background: listTab === 'members' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                color: listTab === 'members' ? '#fff' : '#666',
                fontWeight: 700,
                fontSize: 'clamp(12px, 2.5vw, 14px)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                borderRadius: 'clamp(10px, 2vw, 12px) clamp(10px, 2vw, 12px) 0 0'
              }}
            >
              길드원
            </button>
            <button
              onClick={() => setListTab('records')}
              data-subtab=""
              style={{
                flex: 1,
                padding: 'clamp(10px, 2vw, 12px) 0',
                border: 'none',
                background: listTab === 'records' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                color: listTab === 'records' ? '#fff' : '#666',
                fontWeight: 700,
                fontSize: 'clamp(12px, 2.5vw, 14px)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                borderRadius: 'clamp(10px, 2vw, 12px) clamp(10px, 2vw, 12px) 0 0'
              }}
            >
              전투기록
            </button>
            {hasMap && (
            <button
              onClick={() => setListTab('map')}
              data-subtab=""
              style={{
                flex: 1,
                padding: 'clamp(10px, 2vw, 12px) 0',
                border: 'none',
                background: listTab === 'map' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                color: listTab === 'map' ? '#fff' : '#666',
                fontWeight: 700,
                fontSize: 'clamp(12px, 2.5vw, 14px)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                borderRadius: 'clamp(10px, 2vw, 12px) clamp(10px, 2vw, 12px) 0 0'
              }}
            >
              거점 현황
            </button>
            )}
          </div>

          {/* 공격/방어·승패 고르기는 전투기록 탭 안에 둔다(사용자) — 머리 줄에는 뒤로 가기만 */}
          {listTab === 'records' && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', marginBottom: 'clamp(10px, 2.5vw, 14px)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(5px, 1.2vw, 6px)', flex: 1, minWidth: 0 }}>
                  {/* 상대 길드 — 공격 왼쪽(사용자 2026-10-31). 상대가 둘 이상일 때만, 남는 폭을 쓴다 */}
                  {setSelectedOpp && oppGuilds.length > 1 && (
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <CustomDropdown
                        value={selectedOpp}
                        onChange={(val) => setSelectedOpp(String(val))}
                        options={[{ value: 'all', label: '상대 전체' }, ...oppGuilds.map((g) => ({ value: g, label: g }))]}
                        icon="🏰"
                        compact
                      />
                    </div>
                  )}

                  {/* 공덱/방덱 전환 */}
                  {logType !== undefined && setLogType && (
                    <div style={{ flexShrink: 0, width: 'clamp(92px, 22vw, 105px)' }}>
                      <CustomDropdown
                        value={logType}
                        onChange={(val) => setLogType(Number(val))}
                        options={[
                          { value: 1, label: '공격' },
                          { value: 2, label: '방어' }
                        ]}
                        icon="⚔️"
                        compact
                      />
                    </div>
                  )}

                  {/* 승패 필터 */}
                  <div style={{ flexShrink: 0, width: 'clamp(80px, 18vw, 85px)' }}>
                    <CustomDropdown
                      value={selectedResult}
                      onChange={(val) => setSelectedResult(val)}
                      options={[
                        { value: 'all', label: '전체' },
                        { value: 'my', label: '내 기록' },
                        { value: 'win', label: '승리' },
                        { value: 'lose', label: '패배' }
                      ]}
                      icon={<IconChart />}
                      compact={true}
                    />
                  </div>
                </div>
            </div>
          )}

          <div style={{ width: '100%' }}>
          {/* 게시글 리스트 */}
          {isLoading && initialPosts.length === 0 ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 'clamp(40px, 8vw, 60px)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'clamp(8px, 2vw, 12px)',
                color: '#667eea',
                fontSize: 'clamp(12px, 2.5vw, 14px)',
                fontWeight: 600
              }}>
                <div style={{
                  width: 'clamp(20px, 4vw, 24px)',
                  height: 'clamp(20px, 4vw, 24px)',
                  border: '3px solid rgba(102, 126, 234, 0.2)',
                  borderTopColor: '#667eea',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <span>전투기록을 불러오는 중...</span>
              </div>
              <style>{`
                @keyframes spin {
                  to { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          ) : listTab === 'map' && selectedGroupData?.siegeId ? (
            <SiegeMapTimeline siegeId={selectedGroupData.siegeId} compact={true} />
          ) : listTab === 'members' ? (
            /* 길드원 탭 */
            (() => {
              // 길드원별 승/패 집계 (전체 게시물 사용)
              const memberStats = new Map<string, { nickname: string; profileImg: string; wins: number; losses: number; records: any[]; key: string; isDeleted?: boolean }>();
              postsForMemberStats.forEach((post: any) => {
                const key = post.author || post.nickname;
                if (!memberStats.has(key)) {
                  // 길드를 나간 사람이면 이름을 연한 빨강으로 보여 준다
                  memberStats.set(key, { nickname: post.nickname || '알수없음', profileImg: post.profileImg, wins: 0, losses: 0, records: [], key, isDeleted: post.isDeleted });
                }
                const stat = memberStats.get(key)!;
                if (post.result === 'win') stat.wins++;
                else if (post.result === 'lose') stat.losses++;
                stat.records.push(post);
              });

              // 정렬: 승률 높은 순, 같으면 전투 수 많은 순
              const sortedMembers = Array.from(memberStats.values()).sort((a, b) => {
                const aWinRate = a.wins + a.losses > 0 ? a.wins / (a.wins + a.losses) : 0;
                const bWinRate = b.wins + b.losses > 0 ? b.wins / (b.wins + b.losses) : 0;
                if (bWinRate !== aWinRate) return bWinRate - aWinRate;
                return (b.wins + b.losses) - (a.wins + a.losses);
              });

              return (
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 'clamp(8px, 1.5vw, 10px)' }}>
                  {sortedMembers.map((member, idx) => {
                    const total = member.wins + member.losses;
                    const winRate = total > 0 ? ((member.wins / total) * 100).toFixed(1) : '0.0';
                    const isExpanded = expandedMemberKey === member.key;
                    return (
                      <li
                        key={idx}
                        style={{
                          background: 'rgba(255, 255, 255, 0.9)',
                          borderRadius: 'clamp(12px, 2.5vw, 14px)',
                          border: isExpanded ? '2px solid rgba(102, 126, 234, 0.3)' : '2px solid rgba(102, 126, 234, 0.1)',
                          boxShadow: isExpanded ? '0 6px 20px rgba(102, 126, 234, 0.15)' : '0 3px 10px rgba(102, 126, 234, 0.08)',
                          overflow: 'hidden',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {/* 길드원 헤더 */}
                        <div
                          style={{
                            padding: 'clamp(12px, 2.5vw, 14px) clamp(14px, 3vw, 16px)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'clamp(10px, 2vw, 12px)',
                            cursor: 'pointer',
                            transition: 'background 0.2s ease'
                          }}
                          onClick={(e) => {
                            // 스크롤 위치 저장
                            const scrollY = window.scrollY;
                            setExpandedMemberKey(isExpanded ? null : member.key);
                            // 다음 렌더링 후 스크롤 위치 복원 (여러 번 호출하여 확실하게)
                            requestAnimationFrame(() => {
                              window.scrollTo(0, scrollY);
                              requestAnimationFrame(() => {
                                window.scrollTo(0, scrollY);
                              });
                            });
                          }}
                        >
                          {/* 프로필 */}
                          <img loading="lazy"
                            src={
                              member.profileImg || '/images/default-profile.png'
                            }
                            alt="프로필"
                            style={{ width: 'clamp(36px, 7vw, 40px)', height: 'clamp(36px, 7vw, 40px)', borderRadius: 'clamp(8px, 1.5vw, 10px)', objectFit: 'cover', border: '2px solid rgba(102, 126, 234, 0.2)' }}
                            onError={e => { (e.target as HTMLImageElement).src = '/images/default-profile.png'; }}
                          />
                          {/* 닉네임 */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 'clamp(12px, 2.5vw, 14px)', color: member.isDeleted ? '#ff6b6b' : '#333', marginBottom: 'clamp(2px, 0.5vw, 3px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.nickname}</div>
                            <div style={{ fontSize: 'clamp(10px, 2vw, 11px)', color: '#888' }}>전투 {total}회</div>
                          </div>
                          {/* 승패 통계 */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'clamp(6px, 1.2vw, 8px)',
                            background: 'rgba(0, 0, 0, 0.03)',
                            padding: 'clamp(6px, 1.2vw, 8px) clamp(10px, 2vw, 12px)',
                            borderRadius: 'clamp(8px, 1.5vw, 10px)',
                            flexShrink: 0
                          }}>
                            <span style={{ fontSize: 'clamp(11px, 2.2vw, 12px)', color: '#5a7a9b', fontWeight: 700 }}>{member.wins}승</span>
                            <div style={{ width: 1, height: 'clamp(12px, 2.5vw, 14px)', background: 'rgba(0, 0, 0, 0.1)' }}></div>
                            <span style={{ fontSize: 'clamp(11px, 2.2vw, 12px)', color: '#9b5a6f', fontWeight: 700 }}>{member.losses}패</span>
                            <div style={{ width: 1, height: 'clamp(12px, 2.5vw, 14px)', background: 'rgba(0, 0, 0, 0.1)' }}></div>
                            <span style={{ fontSize: 'clamp(11px, 2.2vw, 12px)', color: '#8b7355', fontWeight: 700 }}>{winRate}%</span>
                          </div>
                          {/* 펼침 아이콘 */}
                          <div style={{
                            fontSize: 'clamp(12px, 2.5vw, 14px)',
                            color: '#667eea',
                            fontWeight: 700,
                            transition: 'transform 0.3s ease',
                            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            flexShrink: 0
                          }}>
                            <Caret />
                          </div>
                        </div>

                        {/* 전투기록 드롭다운 */}
                        {isExpanded && (
                          <div style={{
                            borderTop: '1px solid rgba(102, 126, 234, 0.1)',
                            background: 'rgba(102, 126, 234, 0.02)',
                            padding: 'clamp(12px, 2.5vw, 14px) clamp(14px, 3vw, 16px)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'clamp(8px, 1.5vw, 10px)'
                          }}>
                            {member.records.map((post: any, recordIdx: number) => {
                              const isWin = post.result === 'win';
                              const isLose = post.result === 'lose';
                              return (
                                <div
                                  key={post._id || recordIdx}
                                  style={{
                                    background: isLose ? 'rgba(255, 235, 238, 0.9)' : isWin ? 'rgba(227, 242, 253, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                                    borderRadius: 'clamp(10px, 2vw, 12px)',
                                    padding: 'clamp(10px, 2vw, 12px) clamp(12px, 2.5vw, 14px)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'clamp(8px, 1.5vw, 10px)',
                                    border: isLose ? '1px solid rgba(244, 67, 54, 0.25)' : isWin ? '1px solid rgba(33, 150, 243, 0.25)' : '1px solid rgba(102, 126, 234, 0.15)',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    let newViews = post.views || 0;
                                    try {
                                      const res = await fetch('/api/posts/view', {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ id: post._id })
                                      });
                                      const data = await res.json();
                                      if (data && typeof data.views === 'number') {
                                        newViews = data.views;
                                      }
                                    } catch {}
                                    setSelectedPost({ ...post, views: newViews });
                                  }}
                                >
                                  {/* 공덱 몬스터 (WIN/LOSE 라벨 포함) */}
                                  {post.attackTeam?.length > 0 && (
                                  <div style={{
                                    position: 'relative',
                                    border: isWin ? '1.5px solid #2196F3' : '1.5px solid #f44336',
                                    borderRadius: 'clamp(5px, 1.2vw, 6px)',
                                    padding: 'clamp(8px, 1.8vw, 10px) clamp(3px, 0.8vw, 4px) clamp(3px, 0.8vw, 4px) clamp(3px, 0.8vw, 4px)',
                                    background: isWin ? 'rgba(33, 150, 243, 0.05)' : 'rgba(244, 67, 54, 0.05)',
                                    flexShrink: 0
                                  }}>
                                    <div style={{
                                      position: 'absolute',
                                      top: 'clamp(-6px, -1.2vw, -7px)',
                                      left: '50%',
                                      transform: 'translateX(-50%)',
                                      background: isWin ? '#2196F3' : '#f44336',
                                      color: '#fff',
                                      padding: 'clamp(1px, 0.2vw, 1px) clamp(5px, 1.2vw, 6px)',
                                      borderRadius: 'clamp(3px, 0.8vw, 4px)',
                                      fontSize: 'clamp(6px, 1.5vw, 7px)',
                                      fontWeight: 700,
                                      letterSpacing: '0.3px',
                                      boxShadow: isWin ? '0 1px 3px rgba(33, 150, 243, 0.3)' : '0 1px 3px rgba(244, 67, 54, 0.3)'
                                    }}>
                                      {isWin ? 'WIN' : 'LOSE'}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'clamp(2px, 0.4vw, 3px)' }}>
                                      {post.attackTeam?.map((m: any, mIdx: number) => {
                                        const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                                        return (
                                          <img loading="lazy"
                                            key={mIdx}
                                            src={imgPath ? `/mobs/v3/${imgPath.replace(/\\/g, '/')}` : '/images/empty.png'}
                                            alt={displayName}
                                            style={{
                                              width: 'clamp(20px, 4vw, 24px)',
                                              height: 'clamp(20px, 4vw, 24px)',
                                              borderRadius: 'clamp(3px, 0.8vw, 4px)',
                                              objectFit: 'contain',
                                              background: isWin ? 'linear-gradient(135deg, #fff 0%, #f8faff 100%)' : 'linear-gradient(135deg, #fff 0%, #fff8f8 100%)',
                                              border: isWin ? '1px solid rgba(33, 150, 243, 0.2)' : '1px solid rgba(244, 67, 54, 0.2)'
                                            }}
                                          />
                                        );
                                      })}
                                    </div>
                                  </div>
                                  )}
                                  {/* 한쪽 덱이 없으면 VS 를 그리지 않는다 — 방어 기록에는 상대 공덱이 없다 */}
                                  {post.attackTeam?.length > 0 && post.defenseTeam?.length > 0 && (
                                    <span style={{ color: '#667eea', fontSize: 'clamp(10px, 2vw, 12px)', fontWeight: 600, flexShrink: 0 }}>VS</span>
                                  )}
                                  {/* 방덱 몬스터 (WIN/LOSE 라벨 포함) — 몹이 없으면 칸도 그리지 않는다(빈 테두리만 남아 찌그러졌다) */}
                                  {post.defenseTeam?.length > 0 && (
                                  <div style={{
                                    position: 'relative',
                                    border: isWin ? '1.5px solid #f44336' : '1.5px solid #2196F3',
                                    borderRadius: 'clamp(5px, 1.2vw, 6px)',
                                    padding: 'clamp(8px, 1.8vw, 10px) clamp(3px, 0.8vw, 4px) clamp(3px, 0.8vw, 4px) clamp(3px, 0.8vw, 4px)',
                                    background: isWin ? 'rgba(244, 67, 54, 0.05)' : 'rgba(33, 150, 243, 0.05)',
                                    flexShrink: 0
                                  }}>
                                    <div style={{
                                      position: 'absolute',
                                      top: 'clamp(-6px, -1.2vw, -7px)',
                                      left: '50%',
                                      transform: 'translateX(-50%)',
                                      background: isWin ? '#f44336' : '#2196F3',
                                      color: '#fff',
                                      padding: 'clamp(1px, 0.2vw, 1px) clamp(5px, 1.2vw, 6px)',
                                      borderRadius: 'clamp(3px, 0.8vw, 4px)',
                                      fontSize: 'clamp(6px, 1.5vw, 7px)',
                                      fontWeight: 700,
                                      letterSpacing: '0.3px',
                                      boxShadow: isWin ? '0 1px 3px rgba(244, 67, 54, 0.3)' : '0 1px 3px rgba(33, 150, 243, 0.3)'
                                    }}>
                                      {isWin ? 'LOSE' : 'WIN'}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'clamp(2px, 0.4vw, 3px)' }}>
                                      {post.defenseTeam?.map((m: any, mIdx: number) => {
                                        const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                                        return (
                                          <img loading="lazy"
                                            key={mIdx}
                                            src={imgPath ? `/mobs/v3/${imgPath.replace(/\\/g, '/')}` : '/images/empty.png'}
                                            alt={displayName}
                                            style={{
                                              width: 'clamp(20px, 4vw, 24px)',
                                              height: 'clamp(20px, 4vw, 24px)',
                                              borderRadius: 'clamp(3px, 0.8vw, 4px)',
                                              objectFit: 'contain',
                                              background: isWin ? 'linear-gradient(135deg, #fff 0%, #fff8f8 100%)' : 'linear-gradient(135deg, #fff 0%, #f8faff 100%)',
                                              border: isWin ? '1px solid rgba(244, 67, 54, 0.2)' : '1px solid rgba(33, 150, 243, 0.2)'
                                            }}
                                          />
                                        );
                                      })}
                                    </div>
                                  </div>
                                  )}
                                  {/* 구분선 */}
                                  <div style={{ width: 1, height: 'clamp(16px, 3vw, 18px)', background: 'rgba(0, 0, 0, 0.1)', flexShrink: 0 }} />
                                  {/* 상대 길드 */}
                                  {/* 이름이 길면 이 칸이 줄어든다(점수 딱지를 밀어내지 않게) */}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(4px, 0.8vw, 5px)', flex: '0 1 auto', minWidth: 0 }}>
                                    {post.siegeWar && (
                                      <img loading="lazy"
                                        src={guildImg(post.siegeWarImg || post.siegeWar)}
                                        alt={post.siegeWar}
                                        style={{ width: 'clamp(20px, 4vw, 22px)', height: 'clamp(20px, 4vw, 22px)', borderRadius: 'clamp(4px, 0.8vw, 5px)', objectFit: 'cover', border: '1px solid rgba(102, 126, 234, 0.2)', flexShrink: 0 }}
                                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                      />
                                    )}
                                    <span style={{ fontWeight: 600, fontSize: 'clamp(11px, 2.2vw, 12px)', color: '#667eea', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{post.siegeWar || '-'}</span>
                                  </div>
                                  {/* 공덱 점수 — 전투기록 탭 줄과 같다(사용자 2026-10-26) */}
                                  <div style={{ marginLeft: 'auto', paddingLeft: 4, display: 'flex', flexShrink: 0 }}>
                                    <GradeBadge grade={post.simGrade} win={post.simWin} size={20} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              );
            })()
          ) : (
            initialPosts.map((post: any, idx: number) => {
              /** 방어 기록에는 상대 공덱이 없다 — 그게 방어 기록이라는 표시다 */
              const isDefenseLog = !post.attackTeam || post.attackTeam.length === 0;
              /** 방어 기록의 result 는 막아낸 사람 기준이라 뒤집지 않는다 */
              const defenseWon = isDefenseLog ? post.result === 'win' : post.result !== 'win';
              return (
            <div
              key={post._id}
              style={{
                background: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.8)' : 'rgba(248, 250, 255, 0.8)',
                borderRadius: idx === 0 ? 'clamp(10px, 2vw, 12px) clamp(10px, 2vw, 12px) 0 0' : idx === initialPosts.length - 1 ? '0 0 clamp(10px, 2vw, 12px) clamp(10px, 2vw, 12px)' : 0,
                padding: 'clamp(10px, 2vw, 12px) clamp(5px, 1.2vw, 6px)',
                borderLeft: '2px solid rgba(102, 126, 234, 0.1)',
                borderRight: '2px solid rgba(102, 126, 234, 0.1)',
                borderTop: idx === 0 ? '2px solid rgba(102, 126, 234, 0.1)' : 'none',
                borderBottom: idx === initialPosts.length - 1 ? '2px solid rgba(102, 126, 234, 0.1)' : '1px solid rgba(102, 126, 234, 0.15)',
                display: 'flex',
                alignItems: 'center',
                gap: 'clamp(3px, 0.8vw, 4px)',
                minHeight: 'clamp(56px, 11vh, 60px)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                position: 'relative'
              }}
              onClick={async () => {
                /**
                 * **팝업은 무조건 연다.** (데스크톱 쪽 같은 자리에 이유를 적어 두었다)
                 *
                 * 전투기록은 게시물이 아니라 조회수 요청이 늘 실패하는데, 예전에는
                 * `setSelectedPost` 가 그 응답을 확인하는 `if` 안에 있어서 눌러도
                 * 팝업이 열리지 않았다.
                 */
                const isSiegeRecord = typeof post._id === 'string' && post._id.startsWith('siege-');
                let newViews = post.views || 0;
                if (!isSiegeRecord) {
                  try {
                    const res = await fetch('/api/posts/view', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ id: post._id })
                    });
                    const data = await res.json();
                    if (data && typeof data.views === 'number') newViews = data.views;
                  } catch {}
                }
                setSelectedPost({ ...post, views: newViews });
              }}
              onTouchStart={(e) => {
                e.currentTarget.style.background = 'rgba(102, 126, 234, 0.05)';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.1)';
              }}
              onTouchEnd={(e) => {
                e.currentTarget.style.background = idx % 2 === 0 ? 'rgba(255, 255, 255, 0.8)' : 'rgba(248, 250, 255, 0.8)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* 작성자 */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                gap: 'clamp(2px, 0.5vw, 3px)',
                paddingLeft: 'clamp(3px, 0.8vw, 4px)',
                flexShrink: 0,
                width: '62px',
                minWidth: '62px',
                maxWidth: '62px'
              }}>
                <img loading="lazy"
                  src={post.profileImg || '/images/default-profile.png'}
                  alt={post.nickname}
                  style={{
                    width: 'clamp(26px, 5vw, 28px)',
                    height: 'clamp(26px, 5vw, 28px)',
                    borderRadius: 'clamp(5px, 1.2vw, 6px)',
                    objectFit: 'cover',
                    border: '1px solid rgba(102, 126, 234, 0.2)'
                  }}
                  onError={(e) => {
                    e.currentTarget.src = '/images/default-profile.png';
                  }}
                />
                <span style={{
                  fontSize: 'clamp(8px, 1.8vw, 9px)',
                  // 추방된 사람의 기록은 이름을 빨갛게
                  color: post.isDeleted ? '#ff6b6b' : '#666',
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  textAlign: 'left'
                }}>
                  {post.nickname || '알 수 없음'}
                </span>
                {/* 전투 시각. 칸이 70px 뿐이라 날짜와 시각을 두 줄로 나눈다 —
                    한 줄에 붙이면 `08/10 15:49:19` 가 잘린다 */}
                {post.createdAt && monthDay(post.createdAt) && (
                  <span style={{
                    fontSize: 'clamp(7px, 1.6vw, 8px)',
                    color: '#aaa',
                    fontWeight: 400,
                    lineHeight: 1.25,
                    whiteSpace: 'nowrap',
                    width: '100%',
                    textAlign: 'left'
                  }}>
                    {monthDay(post.createdAt)}
                    <br />
                    {clockTime(post.createdAt, isBattleRecord(post))}
                  </span>
                )}
              </div>

              {/* 공덱 VS 방덱 */}
              {/* 영역: 작성자 62 · 덱(남는 폭) · 상대 58 · 점수 24 — 덱 상자는 내용 폭 그대로라 서로 겹치지 않는다(사용자 2026-10-26) */}
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 'clamp(3px, 1vw, 6px)', justifyContent: 'center', minWidth: 0 }}>
                {/* 방어 기록은 상대 공덱이 없다(게임이 안 준다) — 그러면 공덱 칸도 VS 도 그리지 않는다 */}
                {!isDefenseLog && (
                  <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{
                      position: 'relative',
                      border: post.result === 'win' ? '1.5px solid #2196F3' : '1.5px solid #f44336',
                      borderRadius: 'clamp(5px, 1.2vw, 6px)',
                      padding: 'clamp(8px, 1.8vw, 10px) clamp(3px, 0.8vw, 4px) clamp(3px, 0.8vw, 4px) clamp(3px, 0.8vw, 4px)',
                      background: post.result === 'win' ? 'rgba(33, 150, 243, 0.05)' : 'rgba(244, 67, 54, 0.05)'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: 'clamp(-6px, -1.2vw, -7px)',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: post.result === 'win' ? '#2196F3' : '#f44336',
                        color: '#fff',
                        padding: 'clamp(1px, 0.2vw, 1px) clamp(5px, 1.2vw, 6px)',
                        borderRadius: 'clamp(3px, 0.8vw, 4px)',
                        fontSize: 'clamp(6px, 1.5vw, 7px)',
                        fontWeight: 700,
                        letterSpacing: '0.3px',
                        boxShadow: post.result === 'win' ? '0 1px 3px rgba(33, 150, 243, 0.3)' : '0 1px 3px rgba(244, 67, 54, 0.3)'
                      }}>
                        {post.result === 'win' ? 'WIN' : 'LOSE'}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(2px, 0.5vw, 2px)' }}>
                        {post.attackTeam.map((m: any, idx: number) => {
                          const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                          return (
                            <div key={idx}>
                              <img loading="lazy"
                                src={imgPath ? `/mobs/v3/${imgPath.replace(/\\/g, '/')}` : `/images/empty.png`}
                                alt={displayName}
                                style={{
                                  width: 'clamp(22px, 4.5vw, 24px)',
                                  height: 'clamp(22px, 4.5vw, 24px)',
                                  borderRadius: 'clamp(3px, 0.8vw, 4px)',
                                  objectFit: 'contain',
                                  background: post.result === 'win' ? 'linear-gradient(135deg, #fff 0%, #f8faff 100%)' : 'linear-gradient(135deg, #fff 0%, #fff8f8 100%)',
                                  border: post.result === 'win' ? '1px solid rgba(33, 150, 243, 0.2)' : '1px solid rgba(244, 67, 54, 0.2)',
                                  boxShadow: post.result === 'win' ? '0 1px 3px rgba(33, 150, 243, 0.1)' : '0 1px 3px rgba(244, 67, 54, 0.1)'
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {!isDefenseLog && post.defenseTeam?.length > 0 && (
                  <div style={{
                    flexShrink: 0,
                    padding: 'clamp(3px, 0.8vw, 4px) clamp(1px, 0.6vw, 4px)',
                    color: '#667eea',
                    fontWeight: 600,
                    fontSize: 'clamp(10px, 2vw, 12px)',
                    letterSpacing: '1px',
                    zIndex: 10
                  }}>
                    VS
                  </div>
                )}

                {/* 방어팀 — 방어 기록이면 이 칸만 보인다 */}
                {post.defenseTeam?.length > 0 && (
                  <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{
                      position: 'relative',
                      border: post.result === 'win' ? '1.5px solid #f44336' : '1.5px solid #2196F3',
                      borderRadius: 'clamp(5px, 1.2vw, 6px)',
                      padding: 'clamp(8px, 1.8vw, 10px) clamp(3px, 0.8vw, 4px) clamp(3px, 0.8vw, 4px) clamp(3px, 0.8vw, 4px)',
                      background: defenseWon ? 'rgba(33, 150, 243, 0.05)' : 'rgba(244, 67, 54, 0.05)'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: 'clamp(-6px, -1.2vw, -7px)',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: defenseWon ? '#2196F3' : '#f44336',
                        color: '#fff',
                        padding: 'clamp(1px, 0.2vw, 1px) clamp(5px, 1.2vw, 6px)',
                        borderRadius: 'clamp(3px, 0.8vw, 4px)',
                        fontSize: 'clamp(6px, 1.5vw, 7px)',
                        fontWeight: 700,
                        letterSpacing: '0.3px',
                        boxShadow: post.result === 'win' ? '0 1px 3px rgba(244, 67, 54, 0.3)' : '0 1px 3px rgba(33, 150, 243, 0.3)'
                      }}>
                        {defenseWon ? 'WIN' : 'LOSE'}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(2px, 0.5vw, 2px)' }}>
                        {post.defenseTeam.map((m: any, idx: number) => {
                          const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                          return (
                            <div key={idx}>
                              <img loading="lazy"
                                src={imgPath ? `/mobs/v3/${imgPath.replace(/\\/g, '/')}` : `/images/empty.png`}
                                alt={displayName}
                                style={{
                                  width: 'clamp(22px, 4.5vw, 24px)',
                                  height: 'clamp(22px, 4.5vw, 24px)',
                                  borderRadius: 'clamp(3px, 0.8vw, 4px)',
                                  objectFit: 'contain',
                                  background: post.result === 'win' ? 'linear-gradient(135deg, #fff 0%, #fff8f8 100%)' : 'linear-gradient(135deg, #fff 0%, #f8faff 100%)',
                                  border: post.result === 'win' ? '1px solid rgba(244, 67, 54, 0.2)' : '1px solid rgba(33, 150, 243, 0.2)',
                                  boxShadow: post.result === 'win' ? '0 1px 3px rgba(244, 67, 54, 0.1)' : '0 1px 3px rgba(33, 150, 243, 0.1)'
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 상대 길드 */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                justifyContent: 'center',
                gap: 'clamp(2px, 0.5vw, 3px)',
                paddingRight: 'clamp(3px, 0.8vw, 4px)',
                flexShrink: 0,
                width: '58px',
                minWidth: '58px',
                maxWidth: '58px'
              }}>
                {post.siegeWar && (
                  <>
                    <img loading="lazy"
                      src={guildImg(post.siegeWarImg || post.siegeWar)}
                      alt={post.siegeWar}
                      style={{
                        width: 'clamp(26px, 5vw, 28px)',
                        height: 'clamp(26px, 5vw, 28px)',
                        borderRadius: 'clamp(5px, 1.2vw, 6px)',
                        objectFit: 'cover',
                        border: '1px solid rgba(0, 0, 0, 0.1)'
                      }}
                      onError={(e) => {
                        e.currentTarget.src = '/images/unknown2.png';
                      }}
                    />
                    <span style={{
                      fontSize: 'clamp(8px, 1.8vw, 9px)',
                      color: '#666',
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '100%',
                      textAlign: 'right'
                    }}>
                      {post.siegeWar}
                    </span>
                  </>
                )}
              </div>

              {/* 공덱 점수 — 점령전 공격 기록만(사용자 2026-10-26) */}
              {logType != null && (
                <div style={{ width: 24, minWidth: 24, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexShrink: 0 }}>
                  <GradeBadge grade={post.simGrade} win={post.simWin} size={22} />
                </div>
              )}
            </div>
              );
          })
          )}
          </div>
        </div>
        )}

        {/* 무한 스크롤 트리거 & 로딩 인디케이터 */}
        {battleStep === 'list' && (
        <div ref={observerTarget} style={{
          padding: 'clamp(16px, 3vw, 20px) 0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'clamp(90px, 18vh, 100px)'
        }}>
        </div>
        )}

        {battleStep === 'list' && (
        <CounterPost 
          open={!!selectedPost} 
          onClose={() => setSelectedPost(null)} 
          post={selectedPost} 
          monsterMapping={monsterMapping} 
          user={user} 
          onPostDeleted={onPostDeleted}
        />
        )}

        {/* 업로드 버튼 */}
        {battleStep === 'list' && !selectedPost && (<button
          onClick={onUploadClick}
          style={{
            position: 'fixed',
            right: 'clamp(16px, 3vw, 20px)',
            bottom: 'clamp(16px, 3vw, 20px)',
            width: 'clamp(36px, 7vw, 40px)',
            height: 'clamp(36px, 7vw, 40px)',
            borderRadius: '50%',
            background: '#1976d2',
            color: '#fff',
            fontSize: 'clamp(26px, 5vw, 30px)',
            fontWeight: 500,
            boxShadow: '0 4px 24px #1976d244',
            border: 'none',
            cursor: 'pointer',
            zIndex: 1000,
            opacity: 0.7,
            transition: 'opacity 0.3s ease',
            display: 'none'
          }}
          title="점령전 기록 업로드"
        >+</button>)}
      </div>
    </div>
  );
};

export default PostList; 
